import { Employee } from '../types/employee';
import { supabase } from '../lib/supabase';
import { captureMutationError } from './errorReporting';

export interface ColleagueMeeting {
  id: string;
  requesterId: string;
  matchedId: string;
  suggestedAt: string;
  meetingTime: string | null;
  status: 'suggested' | 'booked' | 'declined';
}

export interface MeetingSlot {
  start: Date;
  end: Date;
  userTime: string;
  colleagueTime: string;
  dayLabel: string;
}

function getUtcOffsetHours(tz: string): number {
  try {
    const ref = new Date('2025-01-15T12:00:00Z');
    const utcStr = ref.toLocaleString('en-US', { timeZone: 'UTC', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const tzStr = ref.toLocaleString('en-US', { timeZone: tz, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const parse = (s: string) => {
      const [date, time] = s.split(', ');
      const [m, d, y] = date.split('/');
      return new Date(`${y}-${m}-${d}T${time === '24:00:00' ? '00:00:00' : time}Z`);
    };
    return (parse(tzStr).getTime() - parse(utcStr).getTime()) / 3_600_000;
  } catch {
    return 0;
  }
}

function formatTimeInTz(date: Date, tz: string): string {
  try {
    return date.toLocaleString('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
}

function formatDayLabel(date: Date, tz: string): string {
  try {
    return date.toLocaleDateString('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
}

export function suggestMeetingSlots(userTz: string, colleagueTz: string): MeetingSlot[] {
  const userOffset = getUtcOffsetHours(userTz);
  const colleagueOffset = getUtcOffsetHours(colleagueTz);

  const userWorkStart = 9 - userOffset;
  const userWorkEnd = 17 - userOffset;
  const colleagueWorkStart = 9 - colleagueOffset;
  const colleagueWorkEnd = 17 - colleagueOffset;

  let overlapStart = Math.max(userWorkStart, colleagueWorkStart);
  let overlapEnd = Math.min(userWorkEnd, colleagueWorkEnd);

  if (overlapEnd - overlapStart < 0.5) {
    const relaxedUserStart = 8 - userOffset;
    const relaxedUserEnd = 18 - userOffset;
    const relaxedColleagueStart = 8 - colleagueOffset;
    const relaxedColleagueEnd = 18 - colleagueOffset;
    overlapStart = Math.max(relaxedUserStart, relaxedColleagueStart);
    overlapEnd = Math.min(relaxedUserEnd, relaxedColleagueEnd);
  }

  if (overlapEnd - overlapStart < 0.5) {
    overlapStart = Math.max(userWorkStart, colleagueWorkStart);
    overlapEnd = overlapStart + 1;
  }

  const slots: MeetingSlot[] = [];
  const now = new Date();
  let candidateDay = new Date(now);
  candidateDay.setUTCDate(candidateDay.getUTCDate() + 1);
  candidateDay.setUTCHours(0, 0, 0, 0);

  let daysChecked = 0;
  while (slots.length < 5 && daysChecked < 14) {
    const dow = candidateDay.getUTCDay();
    if (dow !== 0 && dow !== 6) {
      const rangeHours = overlapEnd - overlapStart - 0.5;
      const randomOffset = Math.random() * Math.max(0, rangeHours);
      const slotHour = overlapStart + randomOffset;
      const startMinutes = Math.round(slotHour * 60 / 30) * 30;

      const slotStart = new Date(candidateDay);
      slotStart.setUTCMinutes(startMinutes);
      const slotEnd = new Date(slotStart);
      slotEnd.setUTCMinutes(slotEnd.getUTCMinutes() + 30);

      slots.push({
        start: slotStart,
        end: slotEnd,
        userTime: formatTimeInTz(slotStart, userTz),
        colleagueTime: formatTimeInTz(slotStart, colleagueTz),
        dayLabel: formatDayLabel(slotStart, userTz),
      });
    }
    candidateDay.setUTCDate(candidateDay.getUTCDate() + 1);
    daysChecked++;
  }

  return slots;
}

export function buildGoogleCalendarUrl(
  userName: string,
  colleagueName: string,
  colleagueEmail: string,
  slot: MeetingSlot,
): string {
  const title = `Coffee Chat: ${userName} & ${colleagueName}`;
  const details = 'A virtual coffee chat to get to know each other better! Arranged via Meet a Colleague.';

  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const dates = `${fmt(slot.start)}/${fmt(slot.end)}`;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates,
    details,
    add: colleagueEmail,
    location: 'Google Meet',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function selectColleague(
  currentUserId: string,
  candidates: Employee[],
  meetingHistory: ColleagueMeeting[],
): Employee | null {
  const eligible = candidates.filter(e => e.id !== currentUserId);
  if (eligible.length === 0) return null;

  const now = Date.now();
  const MAX_WEIGHT = 10000;

  const lastMeetingMap = new Map<string, number>();
  for (const m of meetingHistory) {
    const otherId = m.requesterId === currentUserId ? m.matchedId : m.requesterId;
    const meetDate = new Date(m.suggestedAt).getTime();
    const existing = lastMeetingMap.get(otherId);
    if (!existing || meetDate > existing) {
      lastMeetingMap.set(otherId, meetDate);
    }
  }

  const weights = eligible.map(e => {
    const lastMet = lastMeetingMap.get(e.id);
    if (!lastMet) return MAX_WEIGHT;
    const daysSince = (now - lastMet) / (1000 * 60 * 60 * 24);
    return Math.pow(Math.max(daysSince, 1), 1.5);
  });

  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  let roll = Math.random() * totalWeight;

  for (let i = 0; i < eligible.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return eligible[i];
  }

  return eligible[eligible.length - 1];
}

export async function fetchMeetingHistory(employeeSlug: string): Promise<ColleagueMeeting[]> {
  const { data: empData } = await supabase
    .from('employees')
    .select('id')
    .eq('slug', employeeSlug)
    .maybeSingle();

  if (!empData) return [];

  const { data, error } = await supabase
    .from('colleague_meetings')
    .select('*')
    .or(`requester_id.eq.${empData.id},matched_id.eq.${empData.id}`)
    .order('suggested_at', { ascending: false });

  if (error) return [];

  const allIds = new Set<string>();
  for (const row of data ?? []) {
    allIds.add(row.requester_id);
    allIds.add(row.matched_id);
  }

  const { data: slugData } = await supabase
    .from('employees')
    .select('id, slug')
    .in('id', [...allIds]);

  const idToSlug = new Map<string, string>();
  for (const row of slugData ?? []) {
    idToSlug.set(row.id, row.slug);
  }

  return (data ?? []).map(row => ({
    id: row.id,
    requesterId: idToSlug.get(row.requester_id) ?? row.requester_id,
    matchedId: idToSlug.get(row.matched_id) ?? row.matched_id,
    suggestedAt: row.suggested_at,
    meetingTime: row.meeting_time,
    status: row.status as ColleagueMeeting['status'],
  }));
}

export async function createMeetingSuggestion(requesterSlug: string, matchedSlug: string): Promise<ColleagueMeeting | null> {
  const { data: reqData } = await supabase.from('employees').select('id').eq('slug', requesterSlug).maybeSingle();
  const { data: matchData } = await supabase.from('employees').select('id').eq('slug', matchedSlug).maybeSingle();

  if (!reqData || !matchData) return null;

  const { data, error } = await supabase
    .from('colleague_meetings')
    .insert({ requester_id: reqData.id, matched_id: matchData.id })
    .select()
    .single();

  if (error) {
    captureMutationError(error, { operation: 'createMeetingSuggestion', table: 'colleague_meetings', entityId: reqData.id });
    return null;
  }

  return {
    id: data.id,
    requesterId: data.requester_id,
    matchedId: data.matched_id,
    suggestedAt: data.suggested_at,
    meetingTime: data.meeting_time,
    status: data.status as ColleagueMeeting['status'],
  };
}

export async function updateMeetingStatus(
  meetingId: string,
  status: 'booked' | 'declined',
  meetingTime?: Date,
): Promise<void> {
  const updates: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
  if (meetingTime) updates.meeting_time = meetingTime.toISOString();
  const { error } = await supabase.from('colleague_meetings').update(updates).eq('id', meetingId);
  if (error) {
    captureMutationError(error, { operation: 'updateMeetingStatus', table: 'colleague_meetings', entityId: meetingId, targetStatus: status });
  }
}
